import { formatDate, formatIdr, formatUsd, type TopupWindow } from '@ytclip/shared'
import { CalendarDays, CalendarRange, RefreshCw, Sun, TrendingUp } from 'lucide-react'
import type { ComponentType } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardBody } from '@/components/ui/card.tsx'
import { ErrorState, LoadingBlock } from '@/components/ui/feedback.tsx'
import { PageHeader } from '@/components/ui/misc.tsx'
import { useTopupReport } from '@/features/shared-hooks.ts'

export function AdminReportsPage() {
  const { data, isPending, isError, refetch, isFetching } = useTopupReport()

  return (
    <>
      <PageHeader
        title="Laporan"
        description="Total top-up yang sudah dibayar"
        action={
          <Button
            variant="outlined"
            loading={isFetching}
            onClick={() => void refetch()}
            icon={<RefreshCw className="size-4" />}
          >
            Muat ulang
          </Button>
        }
      />

      {isPending ? (
        <Card>
          <CardBody>
            <LoadingBlock />
          </CardBody>
        </Card>
      ) : isError ? (
        <Card>
          <CardBody>
            <ErrorState message="Laporan tidak bisa dimuat." />
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <TotalCard title="Hari ini" icon={Sun} window={data.today} />
            <TotalCard title="Minggu ini" icon={CalendarDays} window={data.week} />
            <TotalCard title="Bulan ini" icon={CalendarRange} window={data.month} />
          </div>

          <Card>
            <CardBody className="space-y-1">
              <p className="text-body-md text-on-surface">
                Hanya transaksi berstatus lunas yang dihitung, pada saat pembayaran diterima.
              </p>
              <p className="text-label-sm text-on-surface-variant">
                Semua batas hari mengikuti waktu Jakarta ({data.timezone}). Minggu dimulai hari
                Senin.
              </p>
            </CardBody>
          </Card>
        </>
      )}
    </>
  )
}

function TotalCard({
  title,
  icon: Icon,
  window,
}: {
  title: string
  icon: ComponentType<{ className?: string }>
  window: TopupWindow
}) {
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-label-md font-medium text-on-surface-variant">{title}</span>
          <Icon className="size-5 text-primary" />
        </div>

        <p className="text-headline-md font-medium tabular-nums text-on-surface">
          {formatIdr(window.totalIdr)}
        </p>

        <div className="flex items-center gap-2 text-label-md text-on-surface-variant">
          <TrendingUp className="size-4" aria-hidden="true" />
          <span className="tabular-nums">{formatUsd(window.totalUsd)} kredit</span>
          <span aria-hidden="true">·</span>
          <span className="tabular-nums">{window.paidCount} transaksi</span>
        </div>

        <p className="text-label-sm text-on-surface-variant">
          Sejak {formatDate(window.from)}
        </p>
      </CardBody>
    </Card>
  )
}
